import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import 'styles/components/purchase/SeatsPreviewPricing';
import {
  Button,
  Gap,
  Icon,
  OutlineButton,
  Spacing,
  Text,
  TextCollapse,
  Visibility,
} from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import SeatsButtonWrapper from 'ui/atoms/SeatsButtonWrapper';
import NewSeat from 'ui/atoms/NewSeat';
import { useTranslation } from 'react-i18next';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import { discountTypes, earlyDiscountTypes } from 'models/parseTrip';
import { When } from 'react-if';
import { getTripPricing, getTripPricingBySeatPrice } from 'hooks/usePricingBeforeCheckout';
import PricingTooltipDetails from 'ui/atoms/PricingTooltipDetails';
import ToggleLabel from 'ui/molecules/ToggleLabel';
import 'styles/components/SeatsPreviewPricing';
import { isDesktopSize } from 'utils/Reserbus';
import AdjacentBadge from 'images/badges/adjacent-seat.svg';
import AdjacentToggle from 'ui/molecules/AdjacentToggle';
import PricingRow from 'ui/atoms/PricingRow';
import TripDiscountAdvice from 'components/search/TripDiscountAdvice';
import { isPreviewCategoriesEnabled } from 'utils/seats';
import { isResultDiscountUIType, isUrgencyDiscountType } from 'utils/urgencyDiscount';
import useUrgencyDiscountUIEnabled from 'hooks/useUrgencyDiscountUIEnabled';
import useGrowthBookFeatureValue from 'components/GrowthBookProvider/useGrowthBookFeatureValue';
import cn from 'utils/cn';
import { allocateSeatsByCategory } from 'utils/privilegedSeatAllocation';
import { MAX_QUICK_PURCHASE_PASSENGERS } from '../../constants/quickPurchase';
import quickPurchaseIconSrc from '../../images/purchases/quick-purchase.png';
import SeatsAlert from './SeatsAlert';
import SeatsListByZone from './SeatsListByZone';
import CategoriesPricing from './CategoriesPricing';
import { DiscountBadge } from '../search/DynamicPricingDiscount';
import useLoyaltyPrograms from '../../loyalty/context/useLoyaltyPrograms';
import { buildZoneMap, getSeatPriceZonesFromSource, getSeatZone } from '../../utils/seatPricing';
import hasPrivilegedSeats from '../../utils/privilegedSeatDetection';

/**
 * Gets the fare segment assigned to a selected-seat position.
 * @param {Array} fareSegments - Fare allocation segments.
 * @param {number} index - Selected-seat position.
 * @returns {Object|undefined} Assigned fare segment.
 */
const getFareSegmentForSelectionIndex = (fareSegments = [], index = 0) => {
  let startIndex = 0;

  return fareSegments.find((segment) => {
    const endIndex = startIndex + segment.count;
    const isInSegment = index >= startIndex && index < endIndex;
    startIndex = endIndex;

    return isInSegment;
  });
};

/**
 * Resolves selected-seat prices for zone breakdowns when trip discounts override seat prices.
 * @param {Object} options - Helper options.
 * @param {Array} options.selectedSeats - Selected seats.
 * @param {Array} options.fareSegments - Fare allocation segments.
 * @returns {Array} Selected seats with prices resolved for the preview breakdown.
 */
const resolveDiscountAwareSeatPrices = ({ selectedSeats = [], fareSegments = [] }) => {
  let selectedSeatIndex = 0;

  return selectedSeats.map((seat) => {
    if (seat.isPickedAsAdjacent) return seat;

    const fareSegment = getFareSegmentForSelectionIndex(fareSegments, selectedSeatIndex);
    selectedSeatIndex += 1;

    if (!fareSegment) return seat;

    const price =
      fareSegment.type === 'regular' ? seat.price ?? fareSegment.unitTotal : fareSegment.unitTotal;

    return { ...seat, price };
  });
};

/**
 * Calculates the total for non-adjacent selected seats.
 * @param {Array} selectedSeats - Selected seats.
 * @returns {number} Total price for primary seats.
 */
const getPrimarySeatsTotal = (selectedSeats = []) => {
  return selectedSeats
    .filter((seat) => !seat.isPickedAsAdjacent)
    .reduce((sum, seat) => sum + Number(seat.price || 0), 0);
};

/**
 * Aligns pricing preview totals with discount-aware seat prices.
 * @param {Object} options - Helper options.
 * @param {Object} options.pricingPreview - Current pricing preview.
 * @param {Array} options.selectedSeats - Selected seats with resolved prices.
 * @returns {Object} Pricing preview aligned to selected-seat prices.
 */
const resolveDiscountAwarePricingPreview = ({ pricingPreview = {}, selectedSeats = [] }) => {
  const resolvedTotal = getPrimarySeatsTotal(selectedSeats);
  const generalBreakdown = pricingPreview.passengerBreakdown?.general;

  if (!resolvedTotal || !generalBreakdown) return pricingPreview;

  return {
    ...pricingPreview,
    total: resolvedTotal,
    passengerBreakdown: {
      ...pricingPreview.passengerBreakdown,
      general: {
        ...generalBreakdown,
        total: resolvedTotal,
        normalTotal: Math.max(resolvedTotal - (generalBreakdown.totalSpecialDiscount || 0), 0),
      },
    },
  };
};

/**
 * Renders the price preview component.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.tripPricing - The trip pricing.
 * @param {Array<Object>} [props.seatPriceZones] - API seat_price_zones for zone colors.
 * @param {Array} props.seatsSelected - The selected seats.
 * @param {Array} props.passengerTypes - The passenger types.
 * @param {string} props.ctaText - The call to action text.
 * @param {boolean} props.showWomanSwitch
 * @param {Function} props.onWomanSwitchChange
 * @param {boolean} props.womanSwitchValue
 * @param {boolean} props.womanPoliciesError
 * @param {boolean} props.showPetFriendly
 * @param {Function} props.onContinue - The on continue function.
 * @param {Function} props.onSelectAdjacent - The on select adjacent function.
 * @param {boolean} props.adjacentSwitchValue - The adjacent switch value.
 * @param {boolean} props.showAdjacentCard - The flag to show the adjacent card.
 * @param {string} props.id - The slug trip.
 * @param {boolean} props.overbooking - The flag to show the overbooking.
 * @param {Function} props.onQuickPurchaseFromSeats - Quick purchase handler from seat selection.
 * @param {number} props.frequentPassengersCount - Number of frequent passengers available.
 * @param {boolean} props.compact - Render only controls and CTA without pricing details.
 * @returns {JSX.Element} The rendered component.
 */
function SeatsPreviewPricing({
  tripPricing,
  seatPriceZones,
  seatsSelected,
  passengerTypes,
  ctaText,
  showWomanSwitch,
  onWomanSwitchChange,
  womanSwitchValue,
  womanPoliciesError,
  showPetFriendly,
  onContinue,
  onSelectAdjacent,
  adjacentSwitchValue,
  showAdjacentCard,
  id,
  overbooking,
  onQuickPurchaseFromSeats,
  frequentPassengersCount,
  compact,
}) {
  const { features } = useSelector((state) => state.whitelabelConfig);
  const halfPriceCategoryFallback = useGrowthBookFeatureValue('half_price_category_fallback');
  const urgencyDiscountUIEnabled = useUrgencyDiscountUIEnabled();
  const { tripsWithWalletDiscount, thereAreTripsWithWalletDiscount } = useLoyaltyPrograms();
  const typeCategory = features.ENABLE_CATEGORIES;
  const passengerSelection = useSelector((state) => state.search.toJS().passengers);
  const { couponCode, discountedTripsByCoupon } = useSelector((state) => state.search.toJS());
  const discountedTrips = thereAreTripsWithWalletDiscount
    ? tripsWithWalletDiscount
    : discountedTripsByCoupon;

  if (couponCode || thereAreTripsWithWalletDiscount) {
    const tripDiscounted = discountedTrips[couponCode].find((item) => id === item.id) || {};
    if (tripDiscounted && tripPricing) tripPricing = tripDiscounted;
  }

  // Calculate passenger counts
  const passengersCount = seatsSelected.filter((seat) => !seat.isPickedAsAdjacent).length;
  const adjacentSeats = seatsSelected.filter((seat) => seat.isPickedAsAdjacent);

  // Quick purchase from seats visibility and disabled state
  const hasEnoughFrequentPassengers = frequentPassengersCount >= passengersCount;
  const showQuickPurchaseFromSeats = Boolean(
    onQuickPurchaseFromSeats &&
      passengersCount > 0 &&
      passengersCount <= MAX_QUICK_PURCHASE_PASSENGERS &&
      frequentPassengersCount > 0,
  );
  const isQuickPurchaseDisabled = !hasEnoughFrequentPassengers;

  const useSeatBasedPricing = Boolean(features.USE_SEAT_BASED_PRICING);
  const seatsHavePrice = seatsSelected.some((seat) => seat.price != null);
  const hasSeatPriceZones = seatPriceZones && seatPriceZones.length > 0;
  const hasSeatLevelPricingData = seatsHavePrice || hasSeatPriceZones;
  const hasUrgencyDiscount = isUrgencyDiscountType(tripPricing?.discountType);
  const shouldUseSeatPricePreview =
    useSeatBasedPricing &&
    !hasUrgencyDiscount &&
    seatsSelected.length > 0 &&
    hasSeatLevelPricingData;

  const resolvedSeatPriceZones = getSeatPriceZonesFromSource({ seatPriceZones });
  const hasPrimarySeatsForZoneList = passengersCount > 0;
  const showZoneListInCategories = useSeatBasedPricing && hasPrimarySeatsForZoneList;
  const isPrivilegedSeatFlow = useSeatBasedPricing && hasPrivilegedSeats(seatsSelected);

  // Choose the appropriate pricing calculation method based on scenario
  let pricingPreview;

  if (shouldUseSeatPricePreview) {
    pricingPreview = getTripPricingBySeatPrice({
      selectedSeats: seatsSelected,
      pricing: tripPricing,
      passengerSelection,
    });
  } else {
    pricingPreview = getTripPricing({
      passengersCount,
      pricing: tripPricing,
      passengerSelection,
      passengerTypes,
      typeCategory,
      overbooking,
      halfPriceFallbackEnabled: Boolean(halfPriceCategoryFallback),
      mixDiscountFaresByAvailability: Boolean(features.MIX_DISCOUNT_FARES_BY_AVAILABILITY),
    });
  }

  const shouldResolveDiscountAwareSeatPrices = Boolean(
    useSeatBasedPricing &&
      hasUrgencyDiscount &&
      hasSeatLevelPricingData &&
      pricingPreview.fareAllocation?.fareSegments?.length,
  );
  const seatsSelectedForZoneList = shouldResolveDiscountAwareSeatPrices
    ? resolveDiscountAwareSeatPrices({
        selectedSeats: seatsSelected,
        fareSegments: pricingPreview.fareAllocation.fareSegments,
      })
    : seatsSelected;

  if (shouldResolveDiscountAwareSeatPrices) {
    pricingPreview = resolveDiscountAwarePricingPreview({
      pricingPreview,
      selectedSeats: seatsSelectedForZoneList,
    });
  }

  const {
    total = 0,
    totalBeforeDiscount = 0,
    discount = 0,
    totalPercentDiscount,
    discountType,
    luggage = 0,
    taxes = 0,
    networks = 0,
    insurance = 0,
    passengerBreakdown,
  } = pricingPreview;

  const isFlat = features.FUNNEL_STYLE === 'FLAT';

  const { t } = useTranslation();
  const { brand } = useWhitelabelEnvs();
  const { VALID_CATEGORIES, PRICING_BEFORE_CHECKOUT, HIDE_PRICE_DETAILS_TOOLTIP } =
    useWhitelabelFeatures();
  const isDesktop = isDesktopSize();

  const validCategories =
    VALID_CATEGORIES?.filter((category) => category !== 'general' && category !== 'pet_friendly') ||
    [];

  const categories = validCategories
    .slice(0, 3)
    .map((category) => t(`passengers:type.${category}`, { context: 'simple' }))
    .join(', ');

  const firstText = `${t('general:the_discount_for')} `;
  const firstTextFormatted = !isDesktop
    ? `${firstText.charAt(0).toLocaleLowerCase()}${firstText.slice(1)}`
    : firstText;

  const validDiscount = discountTypes[discountType];
  const discountIcon = validDiscount?.icon?.[brand];

  const passengersLabel = `${passengersCount} ${t(
    passengersCount !== 1 ? 'passengers:label.passengers' : 'passengers:label.passenger',
  )}`;
  const hasDiscount = Boolean(total && Number(discount) > 0);
  const hasResolvedDiscountedFare = Boolean(pricingPreview.fareAllocation?.hasDiscountedFare);
  const shouldShowActiveDiscount = hasDiscount && hasResolvedDiscountedFare;
  const showDiscountUrgencyUI =
    shouldShowActiveDiscount && urgencyDiscountUIEnabled && isResultDiscountUIType(discountType);
  const effectiveDiscountIcon = shouldShowActiveDiscount ? discountIcon : null;
  const hasSpecialLabelDiscount = Boolean(effectiveDiscountIcon && validDiscount?.inLabel);
  const discountLabel = hasSpecialLabelDiscount
    ? t(`trips:${validDiscount?.inLabel}`)
    : t('trips:label.save');
  const discountText = `${discountLabel} ${totalPercentDiscount}`;
  const urgencyDiscountAdvice = (
    <Spacing size="XS" alignItems="center">
      {effectiveDiscountIcon && <Icon type={effectiveDiscountIcon} size="M" mobileSize="S" />}
      <DiscountBadge percent={totalPercentDiscount} />
    </Spacing>
  );
  const legacyDiscountAdvice = (
    <TripDiscountAdvice
      discountType={discountType}
      discountAmount={tripPricing.discountBase}
      discountPercent={tripPricing.providerDiscount?.percent}
      tripTotal={tripPricing.total}
      way={id}
    />
  );
  const activeDiscountAdvice = showDiscountUrgencyUI ? urgencyDiscountAdvice : legacyDiscountAdvice;
  const discountAdviceComponent = shouldShowActiveDiscount ? activeDiscountAdvice : null;

  const categoriesComponent = (
    <When condition={total && categories.length}>
      <Text size="XS" textAlign="start">
        {!isDesktop && (
          <Text size="XS" elementType="span" weight="semibold">
            {`${t('general:approx_price')}, `}
          </Text>
        )}
        {firstTextFormatted}
        <Text size="XS" color="primary" elementType="span">
          {`${categories}, etc. `}
        </Text>{' '}
        {` ${t('general:you_will_added_next')}`}
      </Text>
    </When>
  );

  const hasAdjacentSeats = Boolean(adjacentSeats.length);

  const adjacentsTotal =
    hasAdjacentSeats && adjacentSeats[0].adjacentSeats.price * adjacentSeats.length;
  const totalToUse = hasAdjacentSeats ? total + adjacentsTotal : total;

  /**
   * This is from growthbook and is used to determine if the categories should be shown.
   */
  const baseUseCategories = Boolean(passengerTypes?.length >= 2) && isPreviewCategoriesEnabled();

  const zoneMap = buildZoneMap({ seatPriceZones: resolvedSeatPriceZones });

  const useCategories = baseUseCategories || isPrivilegedSeatFlow;
  const listByZone = showZoneListInCategories ? (
    <SeatsListByZone
      selectedSeats={seatsSelectedForZoneList}
      seatPriceZones={resolvedSeatPriceZones}
      showTotal={false}
    />
  ) : null;

  const womanSwitchComponent = showWomanSwitch && (
    <div data-testid="womanCheck">
      <ToggleLabel
        onChange={onWomanSwitchChange}
        value={womanSwitchValue}
        label={t('seats:confirm_woman')}
        iconType="woman"
        error={womanPoliciesError}
        fillBackground
      />
    </div>
  );

  const adjacentSwitchComponent = showAdjacentCard && (
    <AdjacentToggle onSelect={onSelectAdjacent} value={adjacentSwitchValue} />
  );

  if (compact) {
    return (
      <div className="seats-preview-pricing-compact">
        <Spacing size="S" vertical alignItems="flex-end" fullWidth>
          {womanSwitchComponent}
          {adjacentSwitchComponent}
          <Button
            text={ctaText}
            variant="primary"
            padding="M"
            fontSize="S"
            mobilePadding="XXL"
            borderRadius="S"
            fullWidthOnSmall
            fullWidth
            onClick={onContinue}
          />
        </Spacing>
      </div>
    );
  }

  const detailsTooltipComponent = Boolean(
    total && PRICING_BEFORE_CHECKOUT && !HIDE_PRICE_DETAILS_TOOLTIP,
  ) && (
    <PricingTooltipDetails
      luggage={luggage}
      networks={networks}
      taxes={taxes}
      insurance={insurance}
    />
  );

  const totalComponent = Boolean(totalToUse) && (
    <LocalizedCurrency price={totalToUse} currencyStyle="currency" size="L" weight="bold" />
  );

  const passengerBreakdownEntries = Object.entries(passengerBreakdown).filter(
    ([, value]) => value.count >= 1,
  );
  const regularSelectedSeats = seatsSelected.filter((seat) => !seat.isPickedAsAdjacent);
  const seatAllocation = allocateSeatsByCategory({
    regularSeats: regularSelectedSeats,
    passengerSelection,
  });

  const categoryByTypes = (
    <>
      {passengerBreakdownEntries.map(([key, value]) => {
        const seatsInCategory = seatAllocation[key] || [];

        return (
          <CategoriesPricing
            key={key}
            type={key}
            discount={shouldShowActiveDiscount ? value.discountPercent : null}
            subDiscounts={value.count}
            hasDiscountAnticipated={
              key === 'general' &&
              earlyDiscountTypes.includes(discountType) &&
              effectiveDiscountIcon
            }
            total={value.total}
            countWithSpecialDiscount={value.countWithSpecialDiscount}
            totalSpecialDiscount={value.totalSpecialDiscount}
            countWithoutSpecialDiscount={value.countWithoutSpecialDiscount}
            normalDiscountPercent={value.normalDiscountPercent}
            normalTotal={value.normalTotal}
            zoneSeats={seatsInCategory}
            seatPriceZones={resolvedSeatPriceZones}
            showZoneSubRows={showZoneListInCategories}
            pricingSegments={value.pricingSegments}
          />
        );
      })}
      {hasAdjacentSeats && (
        <PricingRow
          label={t('seats:adjacent_seat.label')}
          price={adjacentsTotal}
          icon={AdjacentBadge}
          isSpaceBetween
        />
      )}
    </>
  );

  const categoriesPanelContent =
    !isPrivilegedSeatFlow && showZoneListInCategories ? listByZone : categoryByTypes;

  const categoriesInfoMobile = (
    <When condition={(total && categories.length) || showZoneListInCategories}>
      <TextCollapse
        hasChevron
        triggerTextCollapsed={t('seats:see_details_pricing')}
        triggerTextExpanded={t('seats:see_less')}
        triggerPosition="top"
        isPositionEnd
      >
        <Spacing size="S" vertical fullWidth flexGrow>
          {shouldShowActiveDiscount && (
            <div style={{ position: 'absolute', left: '0', top: '0' }}>
              {discountAdviceComponent}
            </div>
          )}

          {categoriesPanelContent}
        </Spacing>
      </TextCollapse>
    </When>
  );

  const categoriesInfo = <>{categoriesPanelContent}</>;

  const pricingInfo = (
    <>
      {listByZone}
      {!listByZone && (
        <>
          {hasAdjacentSeats && (
            <PricingRow
              label={t('seats:adjacent_seat.label')}
              price={adjacentsTotal}
              icon={AdjacentBadge}
              isSpaceBetween
            />
          )}

          {hasDiscount ? (
            <PricingRow
              label={discountText}
              price={discount}
              hasDiscount
              isSpaceBetween
              icon={effectiveDiscountIcon}
            />
          ) : null}
        </>
      )}
    </>
  );
  // Activate this button when the changes to make it work with the search widget have been done
  const showEditPassengers = false;

  const ctaEditPassengers = (
    <When condition={showEditPassengers && total && categories.length}>
      <Button
        color="grayStrong"
        isRounded
        minHeight="22px"
        padding="S"
        fontSize="S"
        text="Editar pasajeros"
      />
    </When>
  );

  const disclaimerTotal = (
    <>
      {Boolean(totalToUse) && (
        <div className="flex justify-end mb-2">
          <Text size="XS" color="grayLight" textAlign="right">
            {overbooking
              ? t('general:overbooking_discount_disclaimer')
              : t('general:price_variation_notice')}
          </Text>
        </div>
      )}
    </>
  );

  const quickPurchaseSeatsButtons = showQuickPurchaseFromSeats && (
    <div className="flex flex-col gap-2 w-full pb-2 px-2 lg:px-0">
      <OutlineButton
        isRounded
        variant="primary"
        padding="M"
        fontSize="S"
        fullWidth
        onClick={onContinue}
      >
        <div className="flex items-center justify-center gap-1">
          <div className="h-[23px] w-[1px]" />
          {ctaText}
        </div>
      </OutlineButton>
      <div className="group">
        <OutlineButton
          isRounded
          variant="quickPurchase"
          padding="M"
          fontSize="S"
          fullWidth
          isDisabled={isQuickPurchaseDisabled}
          onClick={() => {
            const seatNumbers = seatsSelected
              .filter((s) => !s.isPickedAsAdjacent)
              .map((s) => s.number);
            onQuickPurchaseFromSeats(passengersCount, seatNumbers);
          }}
          hasShadow
        >
          <div className="flex items-center justify-center gap-1">
            <img src={quickPurchaseIconSrc} alt="" height="auto" width={12} />
            <span
              className={cn(
                'bg-[linear-gradient(115deg,#B3ADFA,#BB75FF)] bg-clip-text text-transparent leading-[28px] group-hover:text-white',
                isQuickPurchaseDisabled && 'text-white',
              )}
            >
              {t('purchase:title.quick_purchase_with_seats', { count: passengersCount })}
            </span>
          </div>
        </OutlineButton>
      </div>
    </div>
  );

  const component = (
    <>
      {useCategories ? (
        <Spacing size="S" vertical>
          <Visibility type="hideForMedium">
            <Spacing justifyContent="space-between" alignItems="center" responsiveSize="XXS">
              <Text weight="bold" size="S">
                {t('general:approx_price')}
              </Text>

              {discountAdviceComponent}
            </Spacing>

            <Spacing size="XS" vertical>
              <When condition={total || showZoneListInCategories}>{categoriesInfo}</When>

              <Spacing size="XXS" vertical alignItems="flex-end">
                {womanSwitchComponent}
                {adjacentSwitchComponent}
                {detailsTooltipComponent}
              </Spacing>
            </Spacing>
          </Visibility>
        </Spacing>
      ) : (
        <Spacing size="S" vertical>
          <Visibility type="hideForMedium">
            <Text weight="bold" size="S">
              {t('general:approx_price')}
            </Text>

            <Spacing size="XS" vertical>
              <When condition={total}>
                <>
                  {!listByZone && (
                    <PricingRow
                      label={passengersLabel}
                      price={totalBeforeDiscount}
                      isSpaceBetween
                    />
                  )}
                  {pricingInfo}
                </>
              </When>

              <Spacing size="XXS" vertical alignItems="flex-end">
                {totalComponent}
                {womanSwitchComponent}
                {adjacentSwitchComponent}
                {detailsTooltipComponent}
                {categoriesComponent}
              </Spacing>
            </Spacing>
          </Visibility>
        </Spacing>
      )}

      {!total && (
        <Visibility type="hideForMedium">
          <Text textAlign="center" color="grayMedium">
            {ctaText}
          </Text>
          <br />
        </Visibility>
      )}

      {(showWomanSwitch || showAdjacentCard) && (
        <Visibility type="showForMobileOnly">
          <div className="switch-mobile">
            {womanSwitchComponent}
            {adjacentSwitchComponent}
          </div>
        </Visibility>
      )}

      {Boolean(seatsSelected.length === 0) && (
        <Visibility type="hideForMedium">
          <SeatsAlert padding="S" titleSize="S" titleMobileSize="M" />
        </Visibility>
      )}

      {useCategories ? (
        <>
          <Visibility type="hideForLargeOnly">
            {showQuickPurchaseFromSeats ? (
              quickPurchaseSeatsButtons
            ) : (
              <SeatsButtonWrapper
                ctaText={ctaText}
                onClick={onContinue}
                showContent={Boolean(seatsSelected.length || !isDesktop)}
              >
                <Spacing size="XS" vertical fullWidth>
                  {Boolean(seatsSelected.length === 0) && (
                    <Visibility type="hideForMedium">
                      <SeatsAlert padding="S" titleSize="S" titleMobileSize="M" />
                    </Visibility>
                  )}
                  <Visibility type="showForMobileOnly">{categoriesInfoMobile}</Visibility>
                  <Visibility type="hideForMobileOnly">{categoriesInfo}</Visibility>
                  {detailsTooltipComponent}

                  <Spacing
                    alignItems="center"
                    justifyContent={showEditPassengers ? 'space-between' : 'flex-end'}
                    fullWidth
                  >
                    {ctaEditPassengers}
                    {totalComponent}
                  </Spacing>
                  {disclaimerTotal}
                </Spacing>
              </SeatsButtonWrapper>
            )}
          </Visibility>

          <Visibility type="hideForMedium">
            <Spacing vertical size="S">
              <Spacing
                justifyContent={showEditPassengers ? 'space-between' : 'flex-end'}
                alignItems="center"
                fullWidth
              >
                {useCategories && (
                  <>
                    {ctaEditPassengers}
                    {totalComponent}
                  </>
                )}
              </Spacing>
              {disclaimerTotal}
              {showQuickPurchaseFromSeats ? (
                quickPurchaseSeatsButtons
              ) : (
                <Button
                  text={ctaText}
                  variant="primary"
                  padding="M"
                  fontSize="S"
                  mobilePadding="XXL"
                  borderRadius="S"
                  fullWidthOnSmall
                  fullWidth
                  onClick={onContinue}
                />
              )}
            </Spacing>
          </Visibility>
        </>
      ) : (
        <>
          {showQuickPurchaseFromSeats ? (
            quickPurchaseSeatsButtons
          ) : (
            <SeatsButtonWrapper
              ctaText={ctaText}
              onClick={onContinue}
              showContent={Boolean(seatsSelected.length || !isDesktop)}
            >
              <Visibility type="hideForLargeOnly">
                <Spacing size="S" vertical>
                  <Visibility type="hideForLargeOnly">
                    <SeatsAlert padding="S" titleSize="S" titleMobileSize="M" />
                  </Visibility>

                  {detailsTooltipComponent}
                  <div style={{ width: '100%' }}>
                    <Spacing size="XS" vertical fullWidth>
                      <When condition={total}>
                        <div className="seats-preview-pricing-mobile-breakdown-collapse">
                          <TextCollapse
                            hasChevron
                            triggerTextCollapsed={t('seats:see_details_pricing')}
                            triggerTextExpanded={t('seats:see_less')}
                            triggerPosition="bottom"
                            isPositionEnd
                          >
                            <div className="seats-preview-pricing-mobile-breakdown-content">
                              <Spacing size="S" vertical fullWidth>
                                <Text size="S" weight="bold">
                                  {t('general:approx_price')}
                                </Text>
                                <Spacing
                                  alignItems="center"
                                  justifyContent="space-between"
                                  fullWidth
                                  size="S"
                                >
                                  <Text size="S" color="grayMedium" weight="regular">
                                    {passengersLabel}
                                  </Text>
                                  <LocalizedCurrency
                                    price={totalBeforeDiscount}
                                    currencyStyle="currency"
                                    size="S"
                                    color="grayMedium"
                                  />
                                </Spacing>
                                {pricingInfo}
                              </Spacing>
                            </div>
                          </TextCollapse>
                        </div>
                      </When>

                      <Spacing alignItems="center" justifyContent="flex-end" fullWidth>
                        {totalComponent}
                      </Spacing>
                    </Spacing>
                  </div>
                </Spacing>
              </Visibility>

              <Visibility type="hideForMedium">
                <Spacing justifyContent="center" alignItems="center">
                  <When condition={seatsSelected.length}>
                    <Text size="S" color="grayMedium" whiteSpace="nowrap">
                      {t('seats:pet_counter:title')}
                    </Text>
                    <Gap size="S">
                      {seatsSelected.map((seat) => {
                        const zone = useSeatBasedPricing
                          ? getSeatZone(seat, zoneMap).zoneNumber
                          : undefined;
                        return (
                          <NewSeat
                            key={seat.number}
                            number={seat.number}
                            category={seat.category}
                            type="selected"
                            zone={zone}
                            isSmall
                            useNewDesign
                            showPetFriendly={showPetFriendly}
                          />
                        );
                      })}
                    </Gap>
                  </When>
                </Spacing>
              </Visibility>
            </SeatsButtonWrapper>
          )}
        </>
      )}
    </>
  );

  return isDesktop ? (
    <div
      className={`seats-preview-pricing ${isFlat ? 'seats-preview-pricing-flat' : ''} ${
        useCategories ? 'seats-preview-pricing-categories' : ''
      }`}
    >
      {component}
    </div>
  ) : (
    component
  );
}

SeatsPreviewPricing.propTypes = {
  tripPricing: PropTypes.object.isRequired,
  seatPriceZones: PropTypes.array,
  seatsSelected: PropTypes.array,
  passengerTypes: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      total: PropTypes.number.isRequired,
    }),
  ),
  ctaText: PropTypes.string,
  showWomanSwitch: PropTypes.bool,
  onWomanSwitchChange: PropTypes.func,
  womanSwitchValue: PropTypes.bool,
  womanPoliciesError: PropTypes.bool,
  showPetFriendly: PropTypes.bool,
  onContinue: PropTypes.func,
  onSelectAdjacent: PropTypes.func,
  adjacentSwitchValue: PropTypes.bool,
  showAdjacentCard: PropTypes.bool,
  id: PropTypes.string,
  overbooking: PropTypes.bool,
  onQuickPurchaseFromSeats: PropTypes.func,
  frequentPassengersCount: PropTypes.number,
  compact: PropTypes.bool,
};

SeatsPreviewPricing.defaultProps = {
  seatPriceZones: [],
  seatsSelected: [],
  ctaText: '',
  onContinue: () => {},
  compact: false,
};

export default SeatsPreviewPricing;
